<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex align-center">
        <h3 class="mb-0">Metadata Management</h3>
        <v-btn class="ml-4" color="success" @click="$router.push('/metadata-management/new')">
          Create
        </v-btn>
        <v-btn
          v-if="selected.length > 0"
          class="ml-4"
          color="error"
          data-testid="metadata-template-delete-selected"
          @click="deleteSelected"
        >
          <v-icon left>mdi-delete</v-icon>
          Delete Selected ({{ selected.length }})
        </v-btn>
        <v-btn class="ml-4" color="info" @click="load">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          label="Search"
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          class="mb-4"
        />

        <div class="mb-2">
          <v-chip color="info" small>
            Showing {{ items.length }} templates
          </v-chip>
        </div>

        <v-data-table
          v-model="selected"
          :loading="loading"
          :headers="headers"
          :items="items"
          :search="search"
          :footer-props="{ itemsPerPageOptions: [30, 50, 100, -1] }"
          show-select
          sort-by="id"
        >
          <template v-slot:item.actions="{ item }">
            <v-icon
              class="mr-2"
              color="primary"
              :data-testid="`metadata-template-edit-${item.id}`"
              @click="openEdit(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              color="warning"
              :data-testid="`metadata-template-delete-${item.id}`"
              @click="deleteTemplate(item)"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';

export default {
  name: 'MetadataManagement',
  components: { BasicTemplate },
  data() {
    return {
      items: [],
      selected: [],
      search: '',
      loading: false,
      editError: '',
      headers: [
        { text: 'Id', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  async created() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.editError = '';
      this.selected = [];
      try {
        await this.$msal.ensureAuthenticated();
        const response = await this.$gameApi.get('/meta-templates');
        if (Array.isArray(response)) {
          this.items = response;
        } else if (Array.isArray(response?.items)) {
          this.items = response.items;
        } else if (Array.isArray(response?.data)) {
          this.items = response.data;
        } else {
          this.items = [];
        }
      } catch (error) {
        console.error('Error loading meta templates:', error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    openEdit(item) {
      const templateId = this.resolveTemplateId(item, item);
      if (!templateId) {
        this.editError = 'Template id is missing. Unable to open template.';
        return;
      }
      this.$router.push(`/metadata-management/${templateId}`);
    },
    resolveTemplateId(item, parsed) {
      return (
        parsed?.id ??
        parsed?.templateId ??
        item?.id ??
        item?.templateId
      );
    },
    async deleteSelected() {
      if (this.selected.length === 0) return;
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete selected templates?',
        text: `This will remove ${this.selected.length} template(s) and all their locales.`,
        showCancelButton: true,
      });
      if (!isConfirmed) return;

      try {
        await this.$msal.ensureAuthenticated();
        const idsToDelete = this.selected.map(item => this.resolveTemplateId(item, item)).filter(Boolean);
        await Promise.all(
          idsToDelete.map(async id => {
            const status = await this.$gameApi.delete(`/meta-templates/${id}`);
            if (status === 400) {
              throw new Error(`Template ${id} has references. You should manage them first, then delete it.`);
            }
                 // Accept both 200 and 204 as success statuses for DELETE
                 if (status !== 200 && status !== 204 && (status < 200 || status >= 300)) {
                   throw new Error(`Failed to delete template ${id}: status ${status}`);
                 }
          })
        );
        this.items = this.items.filter(
          template => !idsToDelete.includes(this.resolveTemplateId(template, template))
        );
        this.selected = [];
        this.$swal.fire({
          icon: 'success',
          title: 'Templates deleted.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error deleting meta templates:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Failed to delete templates',
          text: error?.message || 'Some templates could not be deleted.',
          timer: 3000,
        });
        this.editError = 'Failed to delete templates.';
      }
    },
    async deleteTemplate(item) {
      const templateId = this.resolveTemplateId(item, item);
      if (!templateId) {
        this.editError = 'Template id is missing. Unable to delete template.';
        return;
      }
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete template?',
        text: 'This will remove the template and all its locales.',
        showCancelButton: true,
      });
      if (!isConfirmed) return;

      try {
        await this.$msal.ensureAuthenticated();
        const status = await this.$gameApi.delete(`/meta-templates/${templateId}`);
        if (status === 400) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Cannot delete template',
            text: 'Template has references. You should manage them first, then delete it.',
            timer: 4000,
          });
          return;
        }
        if (status < 200 || status >= 300) {
          throw new Error(`Failed to delete template: status ${status}`);
        }
        this.items = this.items.filter(template => this.resolveTemplateId(template, template) !== templateId);
        this.$swal.fire({
          icon: 'success',
          title: 'Template deleted.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error deleting meta template:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Failed to delete template',
          text: error?.message || 'The template could not be deleted.',
          timer: 3000,
        });
        this.editError = 'Failed to delete template.';
      }
    },
  },
};
</script>
